/** Public workshop report UI — keep keys in sync across fi / en / sv.
 *  Scope: the AI-summary (type=1) report surface + shared report chrome
 *  (header, stats, nav, impact map, pivot points, findings summary).
 *  Two interpolation styles live here on purpose:
 *   - {{var}}  → resolved by t() (useTranslation)
 *   - {token}  → resolved by <EditableText vars={...}> (pivot intros only)
 */

export const reportStringsFi = {
  topbar: {
    edit: 'Muokkaa',
    editStop: 'Lopeta muokkaus',
    editTitle: 'Muokkaa raporttia',
    editStopTitle: 'Lopeta muokkaus',
    print: 'Tulosta',
    printPdf: 'Tulosta PDF',
    signIn: 'Kirjaudu',
    signInTitle: 'Kirjaudu raporttiin',
    signedIn: 'Kirjautunut',
    signOutTitle: 'Kirjaudu ulos',
    language: 'Raportin kieli',
  },
  aiSummaryTitle: 'AI-yhteenveto',
  loading: 'Ladataan AI-yhteenvetoa...',
  generatedOn: 'AI-yhteenveto generoitu {{date}}',
  header: {
    subtitle: 'Tekoälyn koostama yhteenveto työpajan tuloksista',
    aiLabelAlt: 'Tekoälyllä tuotettu sisältö',
    date: 'Raportti päivitetty',
    location: 'Paikka',
    participants: 'Osallistujat',
    solutions: 'Ratkaisuja',
    organisationsCount: '{{count}} organisaatiota',
  },
  intro: {
    disclaimer: 'Tämä on tekoälyn automaattisesti koostama yhteenveto työpajan tuloksista. AI on analysoinut osallistujien vaikutuspolut, vipupisteet, linssien esteet ja mahdollisuudet sekä ryhmien ratkaisuehdotukset. Sisältö on rikastettu tutkimusviitteillä — lopullinen tulkinta on aina ihmisten käsissä.',
    translationDisclaimer: 'Tämä raportti on tekoälyn automaattisesti kääntämä. Alkuperäinen kieli on {sourceLanguage}.',
    aboutReport: 'Kuution yhteenvetoraporttiin on dokumentoitu työskentelyn pääkohdat. Tekoäly on laatinut raportin ja Sitran asiantuntijat tarkastaneet sen. Osallistujalista ja Kuution käyttämä tietopohja löytyy koosteen lopusta.',
    aboutMethod: 'Kuutio on Sitran kehittämä menetelmä, jonka tavoitteena on tarjota julkiselle sektorille nopea ja toimiva tapa tarttua yhteiskunnallisiin haasteisiin, tarkastella niitä datapohjaisesti ja tulevaisuusorientoituneesti sekä löytää toimivia ratkaisuja. Kuutio auttaa muodostamaan yhteisen tilannekuvan ja kehittämään ratkaisuja datapohjaisesti pitkä aikaväli ja erilaiset näkökulmat huomioiden. Tavoitteena on vahvistaa julkisen sektorin ennakointi- ja innovaatiokykyä sekä kokeilla, voiko tekoäly toimia saumattomana osana innovaatioprosessia.',
    aboutThisKuutio: 'Päivitä tähän muutama kuvaileva lause Kuution aiheesta, järjestämisajankohdasta sekä toimeksiantajasta.',
  },
  phases: {
    scenariosTitle: 'Skenaariot ja tulevaisuuskuvat',
    scenariosIntro: 'Kukin pienryhmä valitsi yhden tulevaisuuskuvan neljästä skenaariosta työstettäväksi.',
    perspectiveTitle: 'Nykytila ja rajaus',
  },
  languageNames: {
    fi: 'suomi',
    en: 'englanti',
    sv: 'suomenruotsi (sv-FI)',
  },
  translation: {
    badge: 'AI-käännös',
    showOriginal: 'Näytä alkuperäinen',
    hideOriginal: 'Piilota alkuperäinen',
  },
  stats: {
    title: 'Tiivistelmä',
    participants: 'Osallistujaa',
    submissions: 'Ajatusta',
    scenarios: 'Skenaariota',
    pivotPoints: 'Vipupistettä',
    obstacles: 'Estettä',
    opportunities: 'Mahdollisuutta',
    solutions: 'Ratkaisua',
    showAllPhotos: 'Näytä kaikki kuvat ({{count}})',
    showFewerPhotos: 'Näytä vähemmän',
  },
  summary: {
    partParticipants: '{{n}} asiantuntijaa',
    partScenarios: '{{n}} skenaariota',
    partSubmissions: '{{n}} ajatusta vaikutuspoluissa',
    overview: 'Työpajassa {{parts}}.',
    pivotsIdentified: ' Keskeisiksi vipupisteiksi tunnistettiin: {{titles}}{{more}}.',
    andMore: ' ja {{n}} muuta',
    solutionsOne: ' Osallistujat tuottivat yhden ratkaisuehdotuksen.',
    solutionsMany: ' Osallistujat tuottivat {{n}} ratkaisuehdotusta.',
    insightTopScenario: 'eniten keskustelua herätti skenaario {{scenario}} ({{n}} ajatusta)',
    insightMoreOpportunities: 'osallistujat tunnistivat enemmän mahdollisuuksia kuin esteitä',
    insightMoreObstacles: 'esteet koettiin mahdollisuuksia hallitsevammiksi',
    insightBalanced: 'esteet ja mahdollisuudet jakautuivat tasaisesti',
    insightDeepChain: 'pisimmät seurausketjut ulottuivat {{n}} askelta eteenpäin',
    insightSevereSolvable: 'haasteet koettiin vakaviksi mutta ratkaistavissa oleviksi',
    insightSevereCautious: 'haasteet koettiin vakaviksi ja ratkaisuihin suhtauduttiin varauksellisesti',
    insightOptimistic: 'ratkaisuihin suhtauduttiin optimistisesti',
    and: 'ja',
  },
  impactMap: {
    title: 'Vaikutuskartta',
    description: 'Kuution vaikutuskarttatyöskentely pohjautuu vakioskenaarioiden tulevaisuuskuviin ja niiden muuttujiin. Tarkoituksena on vaikutuskartan avulla tarkastella aihetta systeemisesti, tehdä näkyväksi syy-seuraussuhteita sekä hahmottaa erilaisia skenaariopolkuja.',
    ideasVars: '{{ideas}} ajatusta · {{vars}} muuttujaa',
    readMore: 'Lue lisää — {{vars}} muuttujaa, {{ideas}} ajatusta',
    hide: 'Piilota',
  },
  pivots: {
    mapLabel: 'Vipupisteet',
    mapHint: 'Lisää screenshot vipupisteistä',
    title: 'Vipupisteet',
    aiHeading: 'AI-analyysin tunnistamat vipupisteet',
    aiBadge: 'AI-analyysi',
    connections: '{{n}} yhteyttä',
    introSelected: '{aiCount} vipupisteestä valittiin {selectedCount} jatkotyöskentelyyn.',
    introIdentified: 'Ensimmäisen päivän vaikutuspoluista tunnistettiin {aiCount} keskeistä vipupistettä, jotka yhdistävät useita skenaarioita.',
    itemsShort: '{{count}} estettä/mahd.',
    showAllPivots: 'Näytä kaikki {{count}} vipupistettä',
    humanTitle: 'Osallistujien muotoilemat vipupisteet',
    smallGroupWork: 'Pienryhmätyö',
    humanIntro:
      'Toisena päivänä pienryhmät muotoilivat omat vipupisteensä AI-analyysin ja keskustelujen pohjalta. Kukin ryhmä kiteytti yhden keskeisen kysymyksen.',
  },
  nav: {
    openMenu: 'Avaa sisällysluettelo',
    closeMenu: 'Sulje sisällysluettelo',
    menuTitle: 'Sisällysluettelo',
    stats: 'Tiivistelmä',
    futureStory: 'Toivottu tulevaisuus',
    phase0: 'Skenaariot ja tulevaisuuskuvat',
    phase1: 'Nykytila ja rajaus',
    scenarios: 'Vaikutuskartta',
    pivotpoints: 'Vipupisteet',
    lenses: 'Esteet ja mahdollisuudet',
    mitajos: 'Mitä jos?',
    solutions: 'Ratkaisut',
    hybrids: 'Yhdistelmäratkaisut',
    participants: 'Osallistujat',
    sources: 'Tietopohja',
    orgActions: 'Toimenpiteet',
    firstSteps: 'Mitä tehdään ensiksi?',
    painPoints: 'Kipupisteiden keruu',
    kipupisteet: 'Ongelmat',
    ankkurit: 'Muutoksen esteet',
    juurisyyt: 'Juurisyyt',
    leveragePoints: 'Vipupisteet ja mittarit',
    vipupisteetV2: 'Vipupisteet',
    foresightBrief: 'Rajaus',
    changeDrivers: 'Muutosvoimat',
    foresightScenarios: 'Tulevaisuuskuvat',
    foresightAssumptions: 'Oletukset ja tarkistuspisteet',
    preferredFuture: 'Tavoiteltava tulevaisuus',
    backcastingPath: 'Toimintapolku',
    stressTest: 'Villi kortti -stressitesti',
    systemicAnalysis: 'Ongelmakartan analyysi',
    solutionProjections: 'Ratkaisujen vaikutus mittareihin',
    solutionsV4: 'Ratkaisut vipupisteittäin',
    futureInterplayV2: 'Ratkaisujen ristivaikutukset vipupisteittäin',
    futureTimelineV2: 'Tulevaisuustestaus vipupisteittäin',
    futureInterplay: 'Ratkaisujen ristivaikutukset',
    futureTimeline: 'Tulevaisuustestaus',
  },
  chainSections: {
    painPointsIntro: 'Osallistujien kirjaamat kipupisteet ryhmiteltyinä. Kartalle nostetut on merkitty.',
    promoted: 'nostettu kartalle',
    inputsCount: '{{count}} kirjausta',
    kipupisteetIntro: 'Ongelmat, joista ongelmakartta lähti liikkeelle.',
    anchorsCount: '{{count}} muutoksen estettä',
    ankkuritIntro: 'Muutoksen esteet, jotka löydettiin ongelmille linssien kautta.',
    noLens: 'Ei linssiä',
    juurisyytIntro: 'Juurisyyt ja huoneen arviot niistä.',
    approve: 'hyväksy',
    reject: 'hylkää',
    refine: 'tarkenna',
    byParticipant: 'osallistujan ehdotus',
    leverageIntro: 'Mittarit, joihin useampi juurisyy vaikuttaa: ongelmakartan vipupisteet.',
    vipupisteetV2Intro: 'Ongelmakartan vipupisteet: kohdat, joissa useampi juurisyy jakaa saman instrumentin ja toimijan, joka voisi muuttaa sitä. Tekoäly muodosti ne osallistujien ongelmista, muutoksen esteistä, juurisyistä ja niiden arvioista; osallistujat keskustelivat niistä.',
    solutionsV4: {
      intro: 'Ryhmät kirjoittivat ratkaisunsa Ongelmakartan vipupisteisiin. Tekoäly arvioi jokaisen: mitä vipupisteitä se liikuttaa ja mihin suuntaan, mihin ongelmiin se tarttuu, ja kolme pistettä. Arvio on keskustelun pohja; huone paransi ratkaisuja sen pohjalta.',
      scores: 'Toteutettavuus {{feasibility}} · Kunnianhimo {{ambition}} · Vaikuttavuus {{effectiveness}}',
      version: 'versio {{version}}',
      moves: 'Liikuttaa',
      asIntended: 'vipupisteen suuntaan',
      against: 'vipupisteen suuntaa vastaan',
      problems: 'Tarttuu ongelmiin',
      solves: 'ratkaisee',
      touches: 'helpottaa',
      earlier: 'Aiemmat vipupisteet',
      kooste: 'Ratkaisut yhdessä',
      tensions: 'Missä ratkaisut vetävät toisiaan vastaan',
      untouched: 'Vipupisteet, joita mikään ratkaisu ei liikuta: {{list}}',
      problemsLeft: 'Ongelmat, joihin mikään ratkaisu ei tartu: {{list}}',
    },
    futureV2: {
      interplayIntro: 'Ennen äänestystä luettiin, miten työpajan ratkaisut vaikuttavat toisiinsa. Suhteet perustuvat ratkaisujen arvioihin: mitä vipupisteitä kukin liikuttaa ja mihin suuntaan, mihin vipupisteeseen se kirjoitettiin ja kuka sen toteuttaa. Jokaisella on perustelu ja epävarmuus.',
      independent: '{{count}} ratkaisuparilla ei arvioitu olevan merkittävää vaikutusta toisiinsa.',
      via: 'Kulkee vipupisteiden kautta: {{list}}',
      uncertainty: 'Epävarmuus: {{text}}',
      relation: { conflict: 'Ristiriita', overlap: 'Päällekkäisyys', competing: 'Kilpailu', enables: 'Mahdollistaa', reinforces: 'Vahvistaa' },
      timelineIntro: 'Työpaja äänesti pidettävistä ratkaisuista, ja valinta lukittiin. Sen jälkeen kuviteltiin, miten lukitut ratkaisut voisivat toteutua kymmenen vuoden aikana kussakin skenaariossa, arvioiden ne realistisesti siinä maailmassa. Kuvitelma, ei ennuste: jokainen kohtalo perustuu ratkaisujen omiin uhkiin ja niiden välisiin suhteisiin.',
      locked: 'Tulevaisuustestaukseen vietiin: {{names}}',
      override: 'Valinta poikkesi äänestyksestä. Perustelu: {{reason}}',
      deleted: '(poistettu ratkaisu)',
      scenario: 'Skenaario: {{name}}',
      effect: 'Vaikuttavuus: {{verdict}}',
      wouldChange: 'Mikä muuttaisi tämän: {{text}}',
      fate: { realised: 'Toteutuu', delayed: 'Viivästyy', diminished: 'Heikkenee', failed: 'Kaatuu' },
      verdict: { marginal: 'ei juuri liikuta ongelmaa', eases: 'lievittää ongelmaa', fixes: 'korjaa ongelman', dissolves: 'poistaa ongelman lähtökohdan' },
      vipupisteetTitle: 'Mitä vipupisteitä joukko liikuttaa eri skenaarioissa',
      holds: '{{name}}: liikkuu suuntaansa {{n}} / {{total}} skenaariossa',
      robustnessTitle: 'Kestävyys: miten ratkaisut pärjäävät eri skenaarioissa',
      robustnessVerdict: 'Yhteenveto',
      robustness: { holds: 'Kestää kaikissa', failsAll: 'Kaatuu kaikissa', fails: 'Kaatuu jossain', diminishedAll: 'Heikkenee kaikissa', varies: 'Vaihtelee', unknown: 'Ei tietoa' },
    },
    pivotTypes: { konsensus: 'Yhteiset näkemykset', kiistanalainen: 'Kiistanalaiset', villi_kortti: 'Villit kortit', sokea_piste: 'Sokeat pisteet' },
    pivotReach: '{{juurisyyt}} juurisyytä · {{ongelmat}} ongelmaa',
    pivotOwner: 'Voisi muuttaa: {{name}}',
    fromKnowledgeBase: 'Tietopohjasta, ei osallistujien juurisyistä',
    pivotChosen: 'Valittu jatkoon',
    kipupisteSpan: '{{count}} ongelman alueelta',
    juurisyytCount: '{{count}} juurisyytä',
    upIsBetter: 'kasvu on parempi',
    upIsWorse: 'kasvu on huonompi',
    analysisOverview: 'Yleiskuva',
    analysisPattern: 'Kuvio',
    analysisContradictions: 'Ristiriidat',
    analysisGaps: 'Aukot',
    analysisRecommendation: 'Suositus',
    projectionsIntro: 'Miten ratkaisut liikuttaisivat vipupisteiden mittareita.',
    contested: 'ratkaisut vetävät eri suuntiin',
    untouched: 'Mikään ratkaisu ei kohdistu näihin: {{list}}',
    improves: 'parantaa',
    worsens: 'heikentää',
    atFiveYears: '5 vuoden päästä: {{value}}',
  },
  footer: {
    generatedOn: 'AI-yhteenveto generoitu {{date}} ·',
  },
  scenarios: {
    mapLabel: 'Vaikutuskartta',
    mapHint: 'Lisää screenshot vaikutuskartasta',
  },
  sources: {
    manualSource: 'Käsin lisätty lähde',
    title: 'Tietopohja',
    intro1:
      'Kuution tekoälyn toiminta perustuu huolellisesti koottuun tietopohjaan. Kun osallistuja kirjaa ajatuksen tai esittää kysymyksen, tekoäly hakee tietopohjasta oleellisimmat lähteet ja muodostaa vastauksensa niiden pohjalta. Työskentely pysyy näin koko ajan kiinni tutkitussa tiedossa. Tekoäly ei tällä hetkellä hae tietoa kaikkialta internetistä.',
    intro2:
      'Tietopohja koostuu kahdesta osasta: kaikille Kuutioille yhteisestä peruspohjasta sekä kunkin Kuution aiheeseen räätälöidystä substanssipohjasta. Substanssipohjan on koostanut Kuution toimeksiantaja. Lisäksi osallistujat ovat voineet tuoda mukaan aiheen kannalta keskeisimpiä lähteitä. Toimeksiantaja kuratoi osallistujien ehdottamat lähteet.',
    totalUsed: 'Yhteensä Kuutio hyödynsi {{count}} lähdettä.',
    adminOverlayNote:
      'Muokkaukset, piilotukset ja käsin lisätyt rivit elävät raportin ylikerroksena (report_edits) — ne eivät päivity RAG-tietokantaan asti. Listan yhtenäistäminen kantatasolle tulee myöhemmin.',
    loading: 'Ladataan tietopohjaa…',
    loadError: 'Tietopohjan lataaminen epäonnistui: {{error}}',
    baseAccordion: 'Perustietopohja — {{count}} lähdettä',
    instanceAccordion: '{{topic}} — {{count}} lähdettä',
    instanceTopicFallback: 'Kuution oma tietopohja',
    empty: 'Ei lähteitä tässä tietopohjassa.',
    colName: 'Nimi',
    colPublisher: 'Julkaisija',
    hiddenBadge: 'Piilotettu',
    editedBadge: 'Muokattu',
    addedBadge: 'Lisätty käsin',
    restoreTitle: 'Palauta näkyväksi',
    hideTitle: 'Piilota',
    show: 'Näytä',
    hide: 'Piilota',
    addSourceTitle: 'Lisää uusi lähde',
    addSource: 'Lisää lähde',
  },
  highlights: {
    participant: 'Osallistuja',
    idea: 'Ajatus',
    obstacle: 'Este',
    solution: 'Ratkaisu',
  },
  executive: {
    keyFindings: 'Tärkeimmät löydökset',
    whatWeLearned: 'Mitä opimme?',
    whatBlocks: 'Mikä estää edistymistä?',
    nextSteps: 'Seuraavat askeleet',
    selectedPerspective: 'Valittu näkökulma',
  },
  excludedNote: '{{count}} virheellistä syötettä jätettiin pois AI-yhteenvedosta.',
  gate: {
    title: 'Osallistujien sisältö',
    hint: 'Kirjaudu yläpalkin Kirjaudu-painikkeesta nähdäksesi tämän osion sisällön.',
  },
  signIn: {
    title: 'Kirjaudu raporttiin',
    hint: 'Syötä työpajan PIN-koodi nähdäksesi osallistujien sisällön. Koodin saat työpajan järjestäjältä.',
    placeholder: 'PIN-koodi',
    submit: 'Kirjaudu',
    error: 'Väärä PIN-koodi.',
    emailHint: 'Jos sinut on kutsuttu lukemaan raporttia, kirjaudu sähköpostiosoitteellasi. Saat kertakäyttöisen koodin sähköpostiisi.',
    useEmail: 'Kirjaudu sähköpostilla',
    usePin: 'Kirjaudu PIN-koodilla',
    noAccess: 'Olet kirjautunut osoitteella {{email}}, mutta sillä ei ole pääsyä tämän raportin suojattuun sisältöön. Kirjaudu PIN-koodilla tai pyydä kutsu järjestäjältä.',
    closeAriaLabel: 'Sulje',
  },
  common: {
    readMore: 'Lue lisää',
    close: 'Sulje',
    hide: 'Piilota',
    showAll: 'Näytä kaikki {{count}}',
  },
  lenses: {
    title: 'Esteet ja mahdollisuudet',
    obstacles: 'Esteet',
    opportunities: 'Mahdollisuudet',
    aiSummary: 'AI-yhteenveto',
    manualSource: 'Käsin lisätty lähde',
    expandMore: 'Näytä kaikki {{count}}',
    expandLess: 'Näytä vähemmän',
    introDefault:
      'Kuution kuusi linssiä auttavat tarkastelemaan löytyneitä vipupisteitä moniulotteisesti ja tunnistamaan niihin liittyviä esteitä ja mahdollisuuksia. Yhteensä tunnistettiin esteitä ja mahdollisuuksia {{lensCount}} eri linssin kautta.',
    humanIntro:
      'Pienryhmät tutkivat vipupisteitä eri linssien kautta ja tunnistivat esteitä ja mahdollisuuksia kunkin vipupisteen ympäriltä.',
    itemsCount: '{{count}} estettä/mahdollisuutta',
    obstaclesCount: '{{count}} estettä',
    opportunitiesCount: '{{count}} mahdollisuutta',
    allItemsTab: 'Kaikki kohdat ({{count}})',
    noSummaryYet:
      'Tälle linssille ei ole vielä koostettu yhteenvetoa. Kaikki kirjatut esteet ja mahdollisuudet löytyvät “Kaikki kohdat” -välilehdeltä.',
    noItemsYet: 'Tälle linssille ei ole kirjattu yksittäisiä esteitä tai mahdollisuuksia.',
    obstaclesWithCount: 'Esteet ({{count}})',
    opportunitiesWithCount: 'Mahdollisuudet ({{count}})',
    obstacleShort: 'Este',
    opportunityShort: 'Mahd.',
    obstacleAria: 'Este',
    opportunityAria: 'Mahdollisuus',
    summaryIdentified:
      'Osallistujat tunnistivat {{obstacleCount}} estettä ja {{opportunityCount}} mahdollisuutta kuuden linssin kautta. Eniten esteitä nousi {{topObstacleLens}}-linssistä ({{topObstacleCount}}) ja eniten mahdollisuuksia {{topOppLens}}-linssistä ({{topOppCount}}).',
    summaryMoreOpportunities: ' Kokonaisuutena mahdollisuudet painottuivat esteitä enemmän.',
    summaryMoreObstacles: ' Kokonaisuutena esteet painottuivat mahdollisuuksia enemmän.',
    summaryBalanced: ' Esteet ja mahdollisuudet jakautuivat tasaisesti.',
  },
  solutions: {
    summaryGroups:
      '{{groupCount}} ryhmää tuotti yhteensä {{solutionCount}} ratkaisuehdotusta.',
    summaryAvgScore: ' (Vanhoissa analyyseissä) keskimääräinen kokonaisarvio on {{avgScore}}/5.',
    summaryTagBased: ' Uudemmissa analyyseissä käytetään laadullisia tunnisteita ilman kokonaispistettä.',
    printFullTitle: 'Ratkaisut — Täydelliset kuvaukset',
    voteTitle: '{{count}} osallistujan ääni',
    showUnvoted: 'Näytä myös muut ratkaisut',
    blockerAnswersTitle: 'Miten ryhmä vastasi esteisiin',
    blockerAnswerLabel: 'Vastaus',
    blockerFallbackNote: 'Tekoälytiivistelmää ei ole vielä muodostettu; alla ryhmän vastaukset esteisiin.',
    blockerNarrativeLabel: 'Estekuvausten yhteenveto',
  },
  hybrids: {
    selectedNote:
      'Alla valitut yhdistelmät (uudemmissa analyyseissä tunnisteet; vanhemmissa vähintään {{minScore}}/5 kokonaispiste).',
    badge: 'Yhdistelmä',
    fallbackSolution: 'Ratkaisu',
    titleLabel: 'Yhdistelmäratkaisun otsikko',
    descriptionLabel: 'Yhdistelmäratkaisun kuvaus',
  },
  whatIf: {
    title: 'Mitä jos?',
    imageAlt: 'Mitä jos -kysymys {{index}}',
    placeholder: 'Kuvapaikan puuttuu',
    addImage: 'Lisää kuva',
    altText: 'Vaihtoehtoinen teksti',
    question: 'Kysymys',
    fromModule: 'Kysymys tulee Mitä jos -moduulista, joten sitä ei muokata raportissa.',
    imageUrl: 'Kuvan URL',
    hint: 'Fasilitaattori täyttää nämä kymmenen "Mitä jos?" -kysymystä työpajassa nousseista teemoista tai tulevaisuuksista.',
    introDefault:
      'Ennakointi on muutosten kokonaiskuvan hahmottamista ja vaihtoehtoisten tulevaisuuksien tutkimista. Yhden keinon tähän tarjoavat heikot signaalit — merkkejä mahdollisista muutoksista, jotka voivat tulevaisuudessa olla merkittäviä. Tulevaisuus on usein yllättävällä tavalla yllättävä. Siksi tarvitsemme kykyä avartaa ajatuksiamme siitä, mitä kaikkea tulevaisuudessa voi olla. Heikot signaalit auttavat kysymään "Mitä jos?". Kuutiossa avarramme ajatteluamme heikkojen signaalien, villien korttien ja ennalta valmisteltujen aiheeseen liittyvien Mitä jos -kysymysten avulla.',
    creditsHint:
      'Kysymykset on tehty Google Gemini Pro:n avulla, mutta ne ovat asiantuntijoiden valitsemia ja korjaamia. Kuvat on tehty Adoben Firefly -tekoälyllä.',
    workshopAction: 'Pohdi kysymyksiä yhdessä ja keksi omia uusia "Mitä jos?" -kysymyksiä.',
  },
  participantWhatIf: {
    title: 'Osallistujien Mitä jos -kysymykset',
    hint: 'Pienryhmissä esitetyt "mitä jos" -kysymykset litteroinnin perusteella.',
  },
  scenarioChoice: {
    title: 'Pienryhmien valinnat',
    hint: 'Kunkin ryhmän puheenvuoro skenaariovalinnastaan litteroinnin perusteella.',
  },
  groupPhoto: {
    defaultAlt: 'Työpajan ryhmäkuva',
    openAriaLabel: 'Avaa ryhmäkuva suurempana',
  },
  participants: {
    header: 'Osallistujat',
    curatedSource: 'Valittu',
    registeredSource: 'Rekisteröity',
    loading: 'Ladataan osallistujia...',
    error: 'Virhe osallistujien lataamisessa',
    extras: '(Lisää tähän vapaamuotoisesti puuttuvia osallistujia tai muita huomioita.)',
    showAll: 'Näytä kaikki {{count}}',
    hideAll: 'Piilota',
    listExport: 'Osallistuja-lista PDF-viennissä',
    intro:
      'Kuution työskentelyyn osallistui joukko aiheen kannalta keskeisiä asiantuntijoita, päättäjiä ja kokemustietoa edustavia toimijoita. Heidän panoksensa muodostaa raportin sisällöllisen ytimen.',
    commissioner: 'Toimeksiantaja',
    team: 'Kuutio-tiimiläiset',
    noListHint:
      'Ei osallistujalistaa saatavilla. Tarkista että data/kuutio_N/participants.md on olemassa ja että instanssin metadata.kuutio_number on asetettu.',
    unnamed: 'Nimetön',
  },
  orgActions: {
    title: 'Toimenpiteet',
    overview: 'Yhteenveto',
    intro: 'Miten aloitamme? Tekoäly on tunnistanut {{orgs}} organisaatiolle yhteensä {{actions}} konkreettista toimenpidettä, jotka edistävät työpajan ratkaisuja kunkin organisaation omien vahvuuksien pohjalta. Näistä {{now}} on toteutettavissa heti, {{soon}} lähiviikkoina ja {{next}} seuraavaksi.',
    allActions: 'Kaikki toimenpiteet {{count}}',
    urgency: 'Kiireellisyys',
    urgencyNow: 'Nyt',
    urgencySoon: 'Pian',
    urgencyLater: 'Myöhemmin',
    effortSmall: 'Pieni',
    effortMedium: 'Keskitaso',
    effortLarge: 'Suuri',
    responsible: 'Vastuussa',
    relatedSolution: 'Ratkaisu: {{name}}',
    expandAll: 'Avaa kaikki',
    collapseAll: 'Sulje kaikki',
    organisationsCount: '{{count}} organisaatiota',
    reasoning: 'Perustelu',
    expandMore: 'Näytä lisää',
    expandLess: 'Näytä vähemmän',
    noActions: 'Ei toimenpiteitä',
  },
  firstSteps: {
    title: 'Mitä tehdään ensiksi?',
    aiDisclaimer: 'AI ehdotus',
    intro: 'Koottujen ratkaisujen ensimmäisen vaiheen (0–3 kk) toimenpiteet organisaatioittain ryhmiteltynä:',
    expandOrg: 'Avaa {{name}}',
    noActions: 'Ei toimenpiteitä tällä aikavälillä',
    expandMore: 'Näytä lisää',
    expandLess: 'Näytä vähemmän',
    zeroMonth: '0–3 kk',
    disclaimer: 'Tämä on tekoälyn tuottama kehotus toimintaan — älä pidä sitä käskynä. :)',
    noOrgAttribution:
      'Toimenpiteet eivät ole attribuoitavissa yksittäisille organisaatioille — katso ratkaisukohtaiset suunnitelmat yksityiskohdista.',
    unattributedNote:
      'Lisäksi {{count}} toimenpidettä, joille ei tunnistettu selvää vastuutahoa — ne näkyvät ratkaisukohtaisissa suunnitelmissa.',
    actionCount: '{{count}} toimenpidettä',
    collaborationCta: 'Ehdotus yhteistyöhön',
    findSolution: 'Etsi ratkaisu, johon voisit heti lähteä mukaan —',
    browseSolutions: 'selaa ratkaisuja',
  },
  analysis: {
    implementationOutlook: 'Toteutusnäkymä',
    lensesTitle: 'Linssit',
  },
  implementationPlan: {
    title: 'Miten aloitamme?',
    aiDisclaimer: 'AI ehdotus',
    firstStep: 'Ensimmäinen askel',
    reasoningLabel: 'Perustelut',
    leadOrg: 'JOHTAVA ORG',
    supportOrg: 'TUKIJA',
    timeline: 'Aikataulu',
    expandTimeline: 'Avaa aikataulu',
    collapseTimeline: 'Sulje aikataulu',
    phase0: '0–3 kk',
    phase1: '3–6 kk',
    phase2: '6–9 kk',
    phase3: '9–12 kk',
    risks: 'Riskit',
    noRisks: 'Ei tunnistettuja riskejä',
    disclaimer: 'Tämä on tekoälyn tuottama kehotus toimintaan — älä pidä sitä käskynä. :)',
    timelineExpand: '12 kuukauden etenemissuunnitelma',
    yearAchievable: 'Mitä vuodessa on mahdollista saavuttaa',
    yearAchieved: 'Mitä vuodessa on saavutettavissa',
    govtTermAchievable: 'Mitä hallituskaudessa on mahdollista saavuttaa',
    govtTermDerived:
      '{{summary}} Hallituskauden aikana tämä tarkoittaa vakiintunutta rakennetta, lainsäädännön kypsymistä ja tuloksen mittaamista valtakunnallisesti — vuosisuunnitelman onnistumisten pohjalta skaalaten.',
    aiAccelerationNote:
      'Tekoälyn ja digitalisaation kiihtyvä kehitys voi nopeuttaa toteutusta merkittävästi — monet perinteisesti vuosia vaatineet hankkeet ovat nyt toteutettavissa kuukausissa.',
    govtTermPlaceholder:
      'Hallituskauden tarkempi visio tuotetaan suunnitelman seuraavassa generointikierroksessa.',
    mitigation: 'Lieventäminen:',
    moreOrgsTitle: '{{count}} muuta organisaatiota',
    moreOrgsShort: '+{{count}} muuta',
  },
  futureStory: {
    title: 'Toivottu tulevaisuus',
    intro: 'Tulevaisuus on tällä hetkellä olemassa vain mielikuvituksessamme. Kuvittelu on jokaisen sisäsyntyinen ominaisuus, joka voi avata ovia lukemattomiin mahdollisiin tulevaisuuksiin. Se mitä kuvittelemme, vaikuttaa tunteisiimme ja toimintaamme nykyhetkessä ja täten tulevaisuuden suuntaan.',
    whatLabel: 'Mitä osallistujat kuvittelivat',
    whatHint: 'Kuvaile mitä osallistujat kuvittelivat — esim. millaiseen tulevaisuuteen aiheen näkökulmasta yhteinen mielikuvitusmatka suuntautui.',
    whatDefault: 'Kuutiossa työskentely alkaa aiheeseen liittyvän toivotun tulevaisuuden kautta. Tässä Kuutiossa osallistujat kuvittelivat…',
    storyLabel: 'Kollektiivinen tulevaisuustarina',
    storyHint: 'Liitä tähän tekoälyn tuottama kollektiivinen tulevaisuustarina, joka on muodostettu osallistujien jakamista muutoksista. Tarina näkyy raportissa sellaisenaan.',
    storyTitle: 'Kollektiivinen tulevaisuustarina',
  },
  transcript: {
    sectionTitle: 'Litterointi',
    perspectiveLabel: 'Valittu näkökulma',
    showAllPerspectives: 'Näytä kaikki {{count}} vaihtoehtoa',
    hideAllPerspectives: 'Piilota vaihtoehdot',
    speaker: 'Puhuja',
    facilitator: 'Fasilitaattori',
    group: 'Ryhmä',
    emptyComments: 'Ei kommentteja.',
    speakersComments: '{{speakers}} puhujaa · {{comments}} kommenttia',
    showSummary: 'Näytä tiivistelmä',
    showFullQuote: 'Näytä koko puheenvuoro',
    hideTimeline: 'Piilota aikajana',
    showAllComments: 'Näytä kaikki {{count}} kommenttia',
  },
};

export const reportStringsEn = {
  topbar: {
    edit: 'Edit',
    editStop: 'Finish editing',
    editTitle: 'Edit report',
    editStopTitle: 'Finish editing',
    print: 'Print',
    printPdf: 'Print PDF',
    signIn: 'Sign in',
    signInTitle: 'Sign in to the report',
    signedIn: 'Signed in',
    signOutTitle: 'Sign out',
    language: 'Report language',
  },
  aiSummaryTitle: 'AI summary',
  loading: 'Loading AI summary…',
  generatedOn: 'AI summary generated {{date}}',
  header: {
    subtitle: 'An AI-compiled summary of the workshop results',
    aiLabelAlt: 'AI-generated content',
    date: 'Report updated',
    location: 'Location',
    participants: 'Participants',
    solutions: 'Solutions',
    organisationsCount: '{{count}} organisations',
  },
  intro: {
    disclaimer: 'This is an automatically AI-compiled summary of the workshop results. The AI has analysed participants’ impact pathways, pivot points, the barriers and opportunities surfaced by the lenses, and the groups’ solution proposals. The content is enriched with research references — the final interpretation always rests with people.',
    translationDisclaimer: 'This report has been automatically translated by AI from the original {sourceLanguage}.',
    aboutReport: 'The main points of the work are documented in the Kuutio summary report. The AI drafted the report and Sitra’s experts reviewed it. The participant list and the knowledge base used by Kuutio can be found at the end of the summary.',
    aboutMethod: 'Kuutio is a method developed by Sitra that aims to give the public sector a fast and effective way to tackle societal challenges, examine them in a data-driven and future-oriented way, and find workable solutions. Kuutio helps build a shared situational picture and develop solutions in a data-driven way, taking the long term and different perspectives into account. The goal is to strengthen the public sector’s foresight and innovation capacity, and to explore whether AI can work as a seamless part of the innovation process.',
    aboutThisKuutio: 'Update here a few descriptive sentences about this Kuutio’s topic, timing, and the commissioning organisation.',
  },
  phases: {
    scenariosTitle: 'Scenarios and future images',
    scenariosIntro: 'Each small group chose one future image from the four scenarios to work on.',
    perspectiveTitle: 'Current state and framing',
  },
  languageNames: {
    fi: 'Finnish',
    en: 'English',
    sv: 'Finland Swedish (sv-FI)',
  },
  translation: {
    badge: 'AI translation',
    showOriginal: 'Show original',
    hideOriginal: 'Hide original',
  },
  stats: {
    title: 'Summary',
    participants: 'Participants',
    submissions: 'Ideas',
    scenarios: 'Scenarios',
    pivotPoints: 'Pivot points',
    obstacles: 'Barriers',
    opportunities: 'Opportunities',
    solutions: 'Solutions',
    showAllPhotos: 'Show all photos ({{count}})',
    showFewerPhotos: 'Show fewer',
  },
  summary: {
    partParticipants: '{{n}} experts',
    partScenarios: '{{n}} scenarios',
    partSubmissions: '{{n}} ideas across the impact pathways',
    overview: 'The workshop brought together {{parts}}.',
    pivotsIdentified: ' The key pivot points identified were: {{titles}}{{more}}.',
    andMore: ' and {{n}} more',
    solutionsOne: ' Participants produced one solution proposal.',
    solutionsMany: ' Participants produced {{n}} solution proposals.',
    insightTopScenario: 'the most discussed scenario was {{scenario}} ({{n}} ideas)',
    insightMoreOpportunities: 'participants identified more opportunities than barriers',
    insightMoreObstacles: 'barriers were seen as more dominant than opportunities',
    insightBalanced: 'barriers and opportunities were evenly balanced',
    insightDeepChain: 'the longest consequence chains extended {{n}} steps forward',
    insightSevereSolvable: 'challenges were seen as serious but solvable',
    insightSevereCautious: 'challenges were seen as serious and solutions were viewed cautiously',
    insightOptimistic: 'solutions were viewed optimistically',
    and: 'and',
  },
  impactMap: {
    title: 'Impact map',
    description: 'Kuutio’s impact-map work is based on the futures images of the standard scenarios and their variables. The purpose of the impact map is to examine the topic systemically, make cause-and-effect relationships visible, and map out different scenario paths.',
    ideasVars: '{{ideas}} ideas · {{vars}} variables',
    readMore: 'Read more — {{vars}} variables, {{ideas}} ideas',
    hide: 'Hide',
  },
  pivots: {
    mapLabel: 'Pivot points',
    mapHint: 'Add a screenshot of the pivot points',
    title: 'Pivot points',
    aiHeading: 'Pivot points identified by AI analysis',
    aiBadge: 'AI analysis',
    connections: '{{n}} connections',
    introSelected: '{selectedCount} of {aiCount} pivot points were chosen for further work.',
    introIdentified: 'From the first day’s impact pathways, {aiCount} key pivot points were identified that connect several scenarios.',
    itemsShort: '{{count}} barriers/opp.',
    showAllPivots: 'Show all {{count}} pivot points',
    humanTitle: 'Pivot points shaped by participants',
    smallGroupWork: 'Small group work',
    humanIntro:
      'On the second day, small groups shaped their own pivot points based on the AI analysis and discussions. Each group distilled one key question.',
  },
  nav: {
    openMenu: 'Open table of contents',
    closeMenu: 'Close table of contents',
    menuTitle: 'Table of contents',
    stats: 'Summary',
    futureStory: 'Desired future',
    phase0: 'Scenarios and futures images',
    phase1: 'Current state and framing',
    scenarios: 'Impact map',
    pivotpoints: 'Pivot points',
    lenses: 'Barriers and opportunities',
    mitajos: 'What if?',
    solutions: 'Solutions',
    hybrids: 'Hybrid solutions',
    participants: 'Participants',
    sources: 'Knowledge base',
    orgActions: 'Actions',
    firstSteps: 'What to do first?',
    painPoints: 'Pain point discovery',
    kipupisteet: 'Problems',
    ankkurit: 'Obstacles to change',
    juurisyyt: 'Root causes',
    leveragePoints: 'Leverage points and metrics',
    vipupisteetV2: 'Leverage points',
    foresightBrief: 'Brief',
    changeDrivers: 'Change drivers',
    foresightScenarios: 'Future images',
    foresightAssumptions: 'Assumptions and checkpoints',
    preferredFuture: 'Preferred future',
    backcastingPath: 'Backcasting path',
    stressTest: 'Wild-card stress test',
    systemicAnalysis: 'Problem map analysis',
    solutionProjections: 'How solutions move the metrics',
    solutionsV4: 'Solutions by leverage point',
    futureInterplayV2: 'How the solutions affect each other, by leverage point',
    futureTimelineV2: 'Future testing by leverage point',
    futureInterplay: 'How the solutions affect each other',
    futureTimeline: 'Future testing',
  },
  chainSections: {
    painPointsIntro: 'The pain points participants logged, grouped. Those promoted to the map are marked.',
    promoted: 'promoted to the map',
    inputsCount: '{{count}} entries',
    kipupisteetIntro: 'The problems the problem map started from.',
    anchorsCount: '{{count}} obstacles to change',
    ankkuritIntro: 'The obstacles to change found for the problems through the lenses.',
    noLens: 'No lens',
    juurisyytIntro: 'Root causes and the room\'s verdicts on them.',
    approve: 'approve',
    reject: 'reject',
    refine: 'refine',
    byParticipant: 'proposed by a participant',
    leverageIntro: 'Metrics that several root causes act on: the problem map\'s leverage points.',
    vipupisteetV2Intro: 'The problem map\'s leverage points: where several root causes share an instrument and an actor who could change it. AI formed them from the participants\' problems, obstacles to change, root causes and their verdicts; the participants discussed them.',
    solutionsV4: {
      intro: 'The groups wrote their solutions on the problem map\'s leverage points. AI assessed each: which leverage points it moves and which way, which problems it takes on, and three scores. The assessment was a basis for discussion; the room improved the solutions from it.',
      scores: 'Feasibility {{feasibility}} · Ambition {{ambition}} · Effectiveness {{effectiveness}}',
      version: 'version {{version}}',
      moves: 'Moves',
      asIntended: 'in the leverage point\'s direction',
      against: 'against the leverage point\'s direction',
      problems: 'Takes on the problems',
      solves: 'solves',
      touches: 'eases',
      earlier: 'Earlier leverage points',
      kooste: 'The solutions together',
      tensions: 'Where the solutions pull against each other',
      untouched: 'Leverage points no solution moves: {{list}}',
      problemsLeft: 'Problems no solution takes on: {{list}}',
    },
    futureV2: {
      interplayIntro: 'Before the vote, the workshop read how its solutions affect each other. The relations rest on the solutions\' assessments: which leverage points each moves and which way, which leverage point it was written on and who carries it out. Each has its reasoning and its uncertainty.',
      independent: '{{count}} pairs of solutions were judged not to affect each other materially.',
      via: 'Runs through the leverage points: {{list}}',
      uncertainty: 'Uncertainty: {{text}}',
      relation: { conflict: 'Conflict', overlap: 'Overlap', competing: 'Competing', enables: 'Enables', reinforces: 'Reinforces' },
      timelineIntro: 'The workshop voted on which solutions to keep, and the choice was locked. Then it imagined how the locked solutions could be carried out over ten years in each scenario, judging them realistically in that world. A vision, not a forecast: every fate rests on the solutions\' own threats and their relations.',
      locked: 'Carried into future testing: {{names}}',
      override: 'The choice differed from the vote. Reason: {{reason}}',
      deleted: '(deleted solution)',
      scenario: 'Scenario: {{name}}',
      effect: 'Effect: {{verdict}}',
      wouldChange: 'What would change this: {{text}}',
      fate: { realised: 'Realised', delayed: 'Delayed', diminished: 'Diminished', failed: 'Fails' },
      verdict: { marginal: 'barely moves the issue', eases: 'eases the issue', fixes: 'fixes the issue', dissolves: 'removes the issue\'s premise' },
      vipupisteetTitle: 'Which leverage points the set moves in the scenarios',
      holds: '{{name}}: moves its way in {{n}} of {{total}} scenarios',
      robustnessTitle: 'Robustness: how the solutions fare across the scenarios',
      robustnessVerdict: 'Summary',
      robustness: { holds: 'Holds in all', failsAll: 'Fails in all', fails: 'Fails in some', diminishedAll: 'Does less in all', varies: 'Varies', unknown: 'Unknown' },
    },
    pivotTypes: { konsensus: 'Shared views', kiistanalainen: 'Contested', villi_kortti: 'Wild cards', sokea_piste: 'Blind spots' },
    pivotReach: '{{juurisyyt}} root causes · {{ongelmat}} problems',
    pivotOwner: 'Could change it: {{name}}',
    fromKnowledgeBase: 'From the knowledge base, not the participants\' root causes',
    pivotChosen: 'Chosen to go forward',
    kipupisteSpan: 'across {{count}} problems',
    juurisyytCount: '{{count}} root causes',
    upIsBetter: 'higher is better',
    upIsWorse: 'higher is worse',
    analysisOverview: 'Overview',
    analysisPattern: 'Pattern',
    analysisContradictions: 'Contradictions',
    analysisGaps: 'Gaps',
    analysisRecommendation: 'Recommendation',
    projectionsIntro: 'How the solutions would move the leverage point metrics.',
    contested: 'solutions pull in different directions',
    untouched: 'No solution targets: {{list}}',
    improves: 'improves',
    worsens: 'worsens',
    atFiveYears: 'In 5 years: {{value}}',
  },
  footer: {
    generatedOn: 'AI summary generated {{date}} ·',
  },
  scenarios: {
    mapLabel: 'Impact map',
    mapHint: 'Add a screenshot of the impact map',
  },
  sources: {
    manualSource: 'Manually added source',
    title: 'Knowledge base',
    intro1:
      'Kuutio’s AI work is based on a carefully compiled knowledge base. When a participant records an idea or asks a question, the AI retrieves the most relevant sources from the knowledge base and forms its response on that basis. This keeps the work grounded in researched knowledge throughout. At present the AI does not search the entire internet.',
    intro2:
      'The knowledge base consists of two parts: a shared foundation for all Kuutio workshops and a substance base tailored to each Kuutio’s topic. The substance base was compiled by the commissioning organisation. Participants could also bring in key sources related to the topic. The commissioning organisation curates the sources proposed by participants.',
    totalUsed: 'In total, Kuutio used {{count}} sources.',
    adminOverlayNote:
      'Edits, hidden rows, and manually added rows live in the report overlay (report_edits) — they are not yet synced to the RAG database. Consolidating the list to the database layer will come later.',
    loading: 'Loading knowledge base…',
    loadError: 'Failed to load knowledge base: {{error}}',
    baseAccordion: 'Base knowledge — {{count}} sources',
    instanceAccordion: '{{topic}} — {{count}} sources',
    instanceTopicFallback: 'Kuutio-specific knowledge base',
    empty: 'No sources in this knowledge base.',
    colName: 'Name',
    colPublisher: 'Publisher',
    hiddenBadge: 'Hidden',
    editedBadge: 'Edited',
    addedBadge: 'Added manually',
    restoreTitle: 'Restore visibility',
    hideTitle: 'Hide',
    show: 'Show',
    hide: 'Hide',
    addSourceTitle: 'Add new source',
    addSource: 'Add source',
  },
  highlights: {
    participant: 'Participant',
    idea: 'Idea',
    obstacle: 'Barrier',
    solution: 'Solution',
  },
  executive: {
    keyFindings: 'Key findings',
    whatWeLearned: 'What did we learn?',
    whatBlocks: 'What blocks progress?',
    nextSteps: 'Next steps',
    selectedPerspective: 'Selected perspective',
  },
  excludedNote: '{{count}} malformed input(s) were left out of the AI summary.',
  gate: {
    title: 'Participant content',
    hint: 'Sign in using the Sign in button at the top to see this section.',
  },
  signIn: {
    title: 'Sign in to the report',
    hint: 'Enter the workshop PIN to see participant content. You can get the code from the workshop organizer.',
    placeholder: 'PIN code',
    submit: 'Sign in',
    error: 'Wrong PIN code.',
    emailHint: 'If you have been invited to read the report, sign in with your email address. We will send you a one-time code.',
    useEmail: 'Sign in with email',
    usePin: 'Sign in with PIN code',
    noAccess: 'You are signed in as {{email}}, but that account has no access to this report\'s protected content. Sign in with the PIN code or ask the organiser for an invitation.',
    closeAriaLabel: 'Close',
  },
  common: {
    readMore: 'Read more',
    close: 'Close',
    hide: 'Hide',
    showAll: 'Show all {{count}}',
  },
  lenses: {
    title: 'Barriers and opportunities',
    obstacles: 'Barriers',
    opportunities: 'Opportunities',
    aiSummary: 'AI summary',
    manualSource: 'Manually added source',
    expandMore: 'Show all {{count}}',
    expandLess: 'Show less',
    introDefault:
      'Kuutio’s six lenses help examine the identified pivot points from multiple angles and surface related barriers and opportunities. In total, barriers and opportunities were identified through {{lensCount}} different lenses.',
    humanIntro:
      'Small groups explored pivot points through different lenses and identified barriers and opportunities around each pivot point.',
    itemsCount: '{{count}} barriers/opportunities',
    obstaclesCount: '{{count}} barriers',
    opportunitiesCount: '{{count}} opportunities',
    allItemsTab: 'All items ({{count}})',
    noSummaryYet:
      'No summary has been compiled for this lens yet. All recorded barriers and opportunities can be found on the “All items” tab.',
    noItemsYet: 'No individual barriers or opportunities have been recorded for this lens.',
    obstaclesWithCount: 'Barriers ({{count}})',
    opportunitiesWithCount: 'Opportunities ({{count}})',
    obstacleShort: 'Barrier',
    opportunityShort: 'Opp.',
    obstacleAria: 'Barrier',
    opportunityAria: 'Opportunity',
    summaryIdentified:
      'Participants identified {{obstacleCount}} barriers and {{opportunityCount}} opportunities across six lenses. Most barriers came from the {{topObstacleLens}} lens ({{topObstacleCount}}) and most opportunities from the {{topOppLens}} lens ({{topOppCount}}).',
    summaryMoreOpportunities: ' Overall, opportunities outweighed barriers.',
    summaryMoreObstacles: ' Overall, barriers outweighed opportunities.',
    summaryBalanced: ' Barriers and opportunities were evenly balanced.',
  },
  solutions: {
    summaryGroups:
      '{{groupCount}} groups produced {{solutionCount}} solution proposals in total.',
    summaryAvgScore: ' In older analyses the average overall score is {{avgScore}}/5.',
    summaryTagBased: ' Newer analyses use qualitative tags without an overall score.',
    printFullTitle: 'Solutions — Full descriptions',
    voteTitle: '{{count}} participant votes',
    showUnvoted: 'Also show other solutions',
    blockerAnswersTitle: 'How the group addressed barriers',
    blockerAnswerLabel: 'Answer',
    blockerFallbackNote: 'An AI summary is not available yet; the group\'s barrier answers are listed below.',
    blockerNarrativeLabel: 'Barrier answers summary',
  },
  hybrids: {
    selectedNote:
      'Selected combinations below (tags in newer analyses; at least {{minScore}}/5 overall score in older ones).',
    badge: 'Combination',
    fallbackSolution: 'Solution',
    titleLabel: 'Hybrid solution title',
    descriptionLabel: 'Hybrid solution description',
  },
  whatIf: {
    title: 'What if?',
    imageAlt: 'What if question {{index}}',
    placeholder: 'Image placeholder missing',
    addImage: 'Add image',
    altText: 'Alternative text',
    question: 'Question',
    fromModule: 'The question comes from the What if module, so it is not edited in the report.',
    imageUrl: 'Image URL',
    hint: 'The facilitator completes these ten "What if?" questions based on themes or futures that emerged in the workshop.',
    introDefault:
      'Foresight is about grasping the big picture of change and exploring alternative futures. Weak signals — signs of possible changes that may become significant in the future — are one way to do this. The future is often surprisingly surprising. That is why we need the ability to broaden our thinking about what the future might hold. Weak signals help us ask "What if?". In Kuutio we broaden our thinking with weak signals, wild cards, and pre-prepared topic-related "What if?" questions.',
    creditsHint:
      'The questions were created with Google Gemini Pro, but they were selected and refined by experts. The images were created with Adobe Firefly AI.',
    workshopAction: 'Reflect on the questions together and come up with your own new "What if?" questions.',
  },
  participantWhatIf: {
    title: 'Participants’ What if questions',
    hint: '"What if" questions raised in small groups, based on the transcript.',
  },
  scenarioChoice: {
    title: 'Small group choices',
    hint: 'Each group’s statement about their scenario choice, based on the transcript.',
  },
  groupPhoto: {
    defaultAlt: 'Workshop group photo',
    openAriaLabel: 'Open group photo larger',
  },
  participants: {
    header: 'Participants',
    curatedSource: 'Curated',
    registeredSource: 'Registered',
    loading: 'Loading participants…',
    error: 'Error loading participants',
    extras: '(Add here any missing participants or other notes.)',
    showAll: 'Show all {{count}}',
    hideAll: 'Hide',
    listExport: 'Participant list in PDF export',
    intro:
      'A group of key experts, decision-makers, and practitioners representing experiential knowledge took part in the Kuutio work. Their contribution forms the substantive core of the report.',
    commissioner: 'Commissioning organisation',
    team: 'Kuutio team',
    noListHint:
      'No participant list available. Check that data/kuutio_N/participants.md exists and that the instance metadata.kuutio_number is set.',
    unnamed: 'Unnamed',
  },
  orgActions: {
    title: 'Actions',
    overview: 'Overview',
    intro: 'How do we start? AI has identified a total of {{actions}} concrete actions for {{orgs}} organisations that advance the workshop solutions based on each organisation’s own strengths. Of these, {{now}} can be done immediately, {{soon}} in the coming weeks, and {{next}} next.',
    allActions: 'All actions {{count}}',
    urgency: 'Urgency',
    urgencyNow: 'Now',
    urgencySoon: 'Soon',
    urgencyLater: 'Later',
    effortSmall: 'Small',
    effortMedium: 'Medium',
    effortLarge: 'Large',
    responsible: 'Responsible',
    relatedSolution: 'Solution: {{name}}',
    expandAll: 'Expand all',
    collapseAll: 'Collapse all',
    organisationsCount: '{{count}} organisations',
    reasoning: 'Reasoning',
    expandMore: 'Show more',
    expandLess: 'Show less',
    noActions: 'No actions',
  },
  firstSteps: {
    title: 'What to do first?',
    aiDisclaimer: 'AI suggestion',
    intro: 'First phase (0–3 months) actions from the compiled solutions, grouped by organisation:',
    expandOrg: 'Expand {{name}}',
    noActions: 'No actions in this timeframe',
    expandMore: 'Show more',
    expandLess: 'Show less',
    zeroMonth: '0–3 months',
    disclaimer: 'This is an AI-generated call to action — don’t treat it as an order. :)',
    noOrgAttribution:
      'Actions cannot be attributed to individual organisations — see solution-specific plans in the details.',
    unattributedNote:
      'Additionally {{count}} actions for which no clear responsible party was identified — they appear in solution-specific plans.',
    actionCount: '{{count}} actions',
    collaborationCta: 'Suggestion for collaboration',
    findSolution: 'Find a solution you could join right away —',
    browseSolutions: 'browse solutions',
  },
  analysis: {
    implementationOutlook: 'Implementation outlook',
    lensesTitle: 'Lenses',
  },
  implementationPlan: {
    title: 'How do we start?',
    aiDisclaimer: 'AI suggestion',
    firstStep: 'First step',
    reasoningLabel: 'Reasoning',
    leadOrg: 'LEAD ORG',
    supportOrg: 'SUPPORTER',
    timeline: 'Timeline',
    expandTimeline: 'Expand timeline',
    collapseTimeline: 'Collapse timeline',
    phase0: '0–3 months',
    phase1: '3–6 months',
    phase2: '6–9 months',
    phase3: '9–12 months',
    risks: 'Risks',
    noRisks: 'No identified risks',
    disclaimer: 'This is an AI-generated call to action — don’t treat it as an order. :)',
    timelineExpand: '12-month progress plan',
    yearAchievable: 'What can be achieved in a year',
    yearAchieved: 'What can be achieved within a year',
    govtTermAchievable: 'What can be achieved during the government term',
    govtTermDerived:
      '{{summary}} During the government term this means an established structure, maturing legislation, and measuring results nationally — scaling based on the successes of the annual plan.',
    aiAccelerationNote:
      'Accelerating AI and digitalisation can significantly speed up implementation — many projects that traditionally took years can now be delivered in months.',
    govtTermPlaceholder:
      'A more detailed government-term vision will be produced in the next plan generation round.',
    mitigation: 'Mitigation:',
    moreOrgsTitle: '{{count}} other organisations',
    moreOrgsShort: '+{{count}} more',
  },
  futureStory: {
    title: 'Desired future',
    intro: 'The future currently exists only in our imagination. Imagination is an innate quality of every person that can open doors to countless possible futures. What we imagine affects our feelings and actions in the present, and thus the direction of the future.',
    whatLabel: 'What participants imagined',
    whatHint: 'Describe what participants imagined — for example, what kind of future the joint imaginative journey took regarding the topic.',
    whatDefault: 'Kuutio work begins by exploring the desired future related to the topic. In this Kuutio, participants imagined…',
    storyLabel: 'Collective future story',
    storyHint: 'Attach the AI-generated collective future story created from the changes shared by participants. The story will appear in the report as is.',
    storyTitle: 'Collective future story',
  },
  transcript: {
    sectionTitle: 'Transcript',
    perspectiveLabel: 'Chosen perspective',
    showAllPerspectives: 'Show all {{count}} options',
    hideAllPerspectives: 'Hide options',
    speaker: 'Speaker',
    facilitator: 'Facilitator',
    group: 'Group',
    emptyComments: 'No comments.',
    speakersComments: '{{speakers}} speakers · {{comments}} comments',
    showSummary: 'Show summary',
    showFullQuote: 'Show full statement',
    hideTimeline: 'Hide timeline',
    showAllComments: 'Show all {{count}} comments',
  },
};

export const reportStringsSv = {
  topbar: {
    edit: 'Redigera',
    editStop: 'Avsluta redigering',
    editTitle: 'Redigera rapporten',
    editStopTitle: 'Avsluta redigering',
    print: 'Skriv ut',
    printPdf: 'Skriv ut PDF',
    signIn: 'Logga in',
    signInTitle: 'Logga in till rapporten',
    signedIn: 'Inloggad',
    signOutTitle: 'Logga ut',
    language: 'Rapportens språk',
  },
  aiSummaryTitle: 'AI-sammandrag',
  loading: 'Laddar AI-sammandrag…',
  generatedOn: 'AI-sammandrag genererat {{date}}',
  header: {
    subtitle: 'Ett AI-sammanställt sammandrag av workshopens resultat',
    aiLabelAlt: 'AI-genererat innehåll',
    date: 'Rapport uppdaterad',
    location: 'Plats',
    participants: 'Deltagare',
    solutions: 'Lösningar',
    organisationsCount: '{{count}} organisationer',
  },
  intro: {
    disclaimer: 'Detta är ett automatiskt AI-sammanställt sammandrag av workshopens resultat. AI:n har analyserat deltagarnas verkningskedjor, vridpunkter, hinder och möjligheter från linserna samt gruppernas lösningsförslag. Innehållet är berikat med forskningsreferenser — den slutliga tolkningen ligger alltid hos människor.',
    translationDisclaimer: 'Den här rapporten har automatiskt översatts av AI från den ursprungliga rapporten på {sourceLanguage}.',
    aboutReport: 'Huvudpunkterna i arbetet är dokumenterade i Kuutios sammandragsrapport. AI:n har utarbetat rapporten och Sitras experter har granskat den. Deltagarlistan och den kunskapsbas som Kuutio använt finns i slutet av sammandraget.',
    aboutMethod: 'Kuutio är en metod utvecklad av Sitra som syftar till att ge den offentliga sektorn ett snabbt och effektivt sätt att ta itu med samhälleliga utmaningar, granska dem datadrivet och framtidsorienterat samt hitta fungerande lösningar. Kuutio hjälper till att bilda en gemensam lägesbild och utveckla lösningar datadrivet, med hänsyn till det långa tidsperspektivet och olika synvinklar. Målet är att stärka den offentliga sektorns förutseende- och innovationsförmåga och att pröva om AI kan fungera som en sömlös del av innovationsprocessen.',
    aboutThisKuutio: 'Uppdatera här några beskrivande meningar om denna Kuutios ämne, tidpunkt och uppdragsgivare.',
  },
  phases: {
    scenariosTitle: 'Scenarier och framtidsbilder',
    scenariosIntro: 'Varje smågrupp valde en framtidsbild av de fyra scenarierna att arbeta med.',
    perspectiveTitle: 'Nuläge och avgränsning',
  },
  languageNames: {
    fi: 'finska',
    en: 'engelska',
    sv: 'finlandssvenska (sv-FI)',
  },
  translation: {
    badge: 'AI-översättning',
    showOriginal: 'Visa original',
    hideOriginal: 'Dölj original',
  },
  stats: {
    title: 'Sammandrag',
    participants: 'Deltagare',
    submissions: 'Idéer',
    scenarios: 'Scenarier',
    pivotPoints: 'Vridpunkter',
    obstacles: 'Hinder',
    opportunities: 'Möjligheter',
    solutions: 'Lösningar',
    showAllPhotos: 'Visa alla bilder ({{count}})',
    showFewerPhotos: 'Visa färre',
  },
  summary: {
    partParticipants: '{{n}} experter',
    partScenarios: '{{n}} scenarier',
    partSubmissions: '{{n}} idéer i verkningskedjorna',
    overview: 'Workshoppen samlade {{parts}}.',
    pivotsIdentified: ' De centrala vridpunkter som identifierades var: {{titles}}{{more}}.',
    andMore: ' och {{n}} till',
    solutionsOne: ' Deltagarna tog fram ett lösningsförslag.',
    solutionsMany: ' Deltagarna tog fram {{n}} lösningsförslag.',
    insightTopScenario: 'mest diskuterat var scenariot {{scenario}} ({{n}} idéer)',
    insightMoreOpportunities: 'deltagarna identifierade fler möjligheter än hinder',
    insightMoreObstacles: 'hindren upplevdes som mer dominerande än möjligheterna',
    insightBalanced: 'hinder och möjligheter fördelade sig jämnt',
    insightDeepChain: 'de längsta konsekvenskedjorna sträckte sig {{n}} steg framåt',
    insightSevereSolvable: 'utmaningarna upplevdes som allvarliga men lösbara',
    insightSevereCautious: 'utmaningarna upplevdes som allvarliga och lösningarna bemöttes med försiktighet',
    insightOptimistic: 'lösningarna bemöttes optimistiskt',
    and: 'och',
  },
  impactMap: {
    title: 'Verkningskarta',
    description: 'Kuutios verkningskartearbete bygger på standardscenariernas framtidsbilder och deras variabler. Syftet med verkningskartan är att granska ämnet systemiskt, synliggöra orsak-verkan-samband och kartlägga olika scenariovägar.',
    ideasVars: '{{ideas}} idéer · {{vars}} variabler',
    readMore: 'Läs mer — {{vars}} variabler, {{ideas}} idéer',
    hide: 'Dölj',
  },
  pivots: {
    mapLabel: 'Vridpunkter',
    mapHint: 'Lägg till en skärmdump av vridpunkterna',
    title: 'Vridpunkter',
    aiHeading: 'Vridpunkter identifierade av AI-analys',
    aiBadge: 'AI-analys',
    connections: '{{n}} kopplingar',
    introSelected: '{selectedCount} av {aiCount} vridpunkter valdes för fortsatt arbete.',
    introIdentified: 'Från den första dagens verkningskedjor identifierades {aiCount} centrala vridpunkter som förenar flera scenarier.',
    itemsShort: '{{count}} hinder/möj.',
    showAllPivots: 'Visa alla {{count}} vridpunkter',
    humanTitle: 'Deltagarnas formade vridpunkter',
    smallGroupWork: 'Smågruppsarbete',
    humanIntro:
      'Den andra dagen formade smågrupperna sina egna vridpunkter utifrån AI-analysen och diskussionerna. Varje grupp destillerade en central fråga.',
  },
  nav: {
    openMenu: 'Öppna innehållsförteckning',
    closeMenu: 'Stäng innehållsförteckning',
    menuTitle: 'Innehållsförteckning',
    stats: 'Sammandrag',
    futureStory: 'Önskad framtid',
    phase0: 'Scenarier och framtidsbilder',
    phase1: 'Nuläge och avgränsning',
    scenarios: 'Verkningskarta',
    pivotpoints: 'Vridpunkter',
    lenses: 'Hinder och möjligheter',
    mitajos: 'Tänk om?',
    solutions: 'Lösningar',
    hybrids: 'Hybridlösningar',
    participants: 'Deltagare',
    sources: 'Kunskapsbas',
    orgActions: 'Åtgärder',
    firstSteps: 'Vad görs först?',
    painPoints: 'Smärtpunktsinsamling',
    kipupisteet: 'Problem',
    ankkurit: 'Hinder för förändring',
    juurisyyt: 'Grundorsaker',
    leveragePoints: 'Hävstångspunkter och mätare',
    vipupisteetV2: 'Hävstångspunkter',
    foresightBrief: 'Avgränsning',
    changeDrivers: 'Förändringskrafter',
    foresightScenarios: 'Framtidsbilder',
    foresightAssumptions: 'Antaganden och kontrollpunkter',
    preferredFuture: 'Önskad framtid',
    backcastingPath: 'Handlingsväg',
    stressTest: 'Stresstest med wild card',
    systemicAnalysis: 'Problemkartans analys',
    solutionProjections: 'Hur lösningarna påverkar mätarna',
    solutionsV4: 'Lösningar per hävstångspunkt',
    futureInterplayV2: 'Hur lösningarna påverkar varandra, per hävstångspunkt',
    futureTimelineV2: 'Framtidstestning per hävstångspunkt',
    futureInterplay: 'Hur lösningarna påverkar varandra',
    futureTimeline: 'Framtidstestning',
  },
  chainSections: {
    painPointsIntro: 'De smärtpunkter deltagarna noterade, grupperade. De som lyfts till kartan är markerade.',
    promoted: 'lyft till kartan',
    inputsCount: '{{count}} anteckningar',
    kipupisteetIntro: 'Problemen som problemkartan utgick från.',
    anchorsCount: '{{count}} hinder för förändring',
    ankkuritIntro: 'De hinder för förändring som hittades för problemen genom linserna.',
    noLens: 'Ingen lins',
    juurisyytIntro: 'Grundorsakerna och rummets bedömningar av dem.',
    approve: 'godkänn',
    reject: 'avvisa',
    refine: 'förfina',
    byParticipant: 'föreslagen av en deltagare',
    leverageIntro: 'Mätare som flera grundorsaker påverkar: problemkartans hävstångspunkter.',
    vipupisteetV2Intro: 'Problemkartans hävstångspunkter: där flera grundorsaker delar ett instrument och en aktör som kunde ändra det. AI bildade dem utifrån deltagarnas problem, hinder för förändring, grundorsaker och bedömningarna av dem; deltagarna diskuterade dem.',
    solutionsV4: {
      intro: 'Grupperna skrev sina lösningar på problemkartans hävstångspunkter. AI bedömde var och en: vilka hävstångspunkter den rör och åt vilket håll, vilka problem den tar itu med, och tre poäng. Bedömningen var ett underlag för samtal; rummet förbättrade lösningarna utifrån den.',
      scores: 'Genomförbarhet {{feasibility}} · Ambition {{ambition}} · Verkan {{effectiveness}}',
      version: 'version {{version}}',
      moves: 'Rör',
      asIntended: 'i hävstångspunktens riktning',
      against: 'mot hävstångspunktens riktning',
      problems: 'Tar itu med problemen',
      solves: 'löser',
      touches: 'lindrar',
      earlier: 'Tidigare hävstångspunkter',
      kooste: 'Lösningarna tillsammans',
      tensions: 'Var lösningarna drar mot varandra',
      untouched: 'Hävstångspunkter som ingen lösning rör: {{list}}',
      problemsLeft: 'Problem som ingen lösning tar itu med: {{list}}',
    },
    futureV2: {
      interplayIntro: 'Före omröstningen lästes hur workshopens lösningar påverkar varandra. Relationerna vilar på lösningarnas bedömningar: vilka hävstångspunkter var och en rör och åt vilket håll, vilken hävstångspunkt den skrevs på och vem som genomför den. Var och en har sin motivering och sin osäkerhet.',
      independent: '{{count}} lösningspar bedömdes inte påverka varandra nämnvärt.',
      via: 'Går via hävstångspunkterna: {{list}}',
      uncertainty: 'Osäkerhet: {{text}}',
      relation: { conflict: 'Konflikt', overlap: 'Överlappning', competing: 'Konkurrens', enables: 'Möjliggör', reinforces: 'Förstärker' },
      timelineIntro: 'Workshopen röstade om vilka lösningar som skulle behållas, och valet låstes. Sedan föreställde man sig hur de låsta lösningarna kunde genomföras under tio år i varje scenario och bedömde dem realistiskt i den världen. En vision, inte en prognos: varje öde vilar på lösningarnas egna hot och deras relationer.',
      locked: 'Togs med till framtidstestningen: {{names}}',
      override: 'Valet avvek från omröstningen. Motivering: {{reason}}',
      deleted: '(borttagen lösning)',
      scenario: 'Scenario: {{name}}',
      effect: 'Effekt: {{verdict}}',
      wouldChange: 'Vad som skulle ändra detta: {{text}}',
      fate: { realised: 'Förverkligas', delayed: 'Försenas', diminished: 'Försvagas', failed: 'Misslyckas' },
      verdict: { marginal: 'rör knappt problemet', eases: 'lindrar problemet', fixes: 'löser problemet', dissolves: 'tar bort problemets förutsättning' },
      vipupisteetTitle: 'Vilka hävstångspunkter helheten rör i scenarierna',
      holds: '{{name}}: rör sig åt sitt håll i {{n}} av {{total}} scenarier',
      robustnessTitle: 'Robusthet: hur lösningarna klarar sig i scenarierna',
      robustnessVerdict: 'Sammanfattning',
      robustness: { holds: 'Håller i alla', failsAll: 'Misslyckas i alla', fails: 'Misslyckas i något', diminishedAll: 'Försvagas i alla', varies: 'Varierar', unknown: 'Okänt' },
    },
    pivotTypes: { konsensus: 'Gemensamma synsätt', kiistanalainen: 'Omstridda', villi_kortti: 'Jokrar', sokea_piste: 'Blinda fläckar' },
    pivotReach: '{{juurisyyt}} grundorsaker · {{ongelmat}} problem',
    pivotOwner: 'Kunde ändra det: {{name}}',
    fromKnowledgeBase: 'Från kunskapsbasen, inte deltagarnas grundorsaker',
    pivotChosen: 'Vald att gå vidare',
    kipupisteSpan: 'från {{count}} problem',
    juurisyytCount: '{{count}} grundorsaker',
    upIsBetter: 'högre är bättre',
    upIsWorse: 'högre är sämre',
    analysisOverview: 'Översikt',
    analysisPattern: 'Mönster',
    analysisContradictions: 'Motsägelser',
    analysisGaps: 'Luckor',
    analysisRecommendation: 'Rekommendation',
    projectionsIntro: 'Hur lösningarna skulle flytta hävstångspunkternas mätare.',
    contested: 'lösningarna drar åt olika håll',
    untouched: 'Ingen lösning riktar sig mot: {{list}}',
    improves: 'förbättrar',
    worsens: 'försämrar',
    atFiveYears: 'Om 5 år: {{value}}',
  },
  footer: {
    generatedOn: 'AI-sammandrag genererat {{date}} ·',
  },
  scenarios: {
    mapLabel: 'Verkningskarta',
    mapHint: 'Lägg till en skärmdump av verkningskartan',
  },
  sources: {
    manualSource: 'Manuellt tillagd källa',
    title: 'Kunskapsbas',
    intro1:
      'Kuutios AI-arbete bygger på en noggrant sammanställd kunskapsbas. När en deltagare registrerar en idé eller ställer en fråga hämtar AI:n de mest relevanta källorna från kunskapsbasen och formulerar sitt svar utifrån dem. Arbetet hålls så hela tiden förankrat i forskad kunskap. För närvarande söker AI:n inte information från hela internet.',
    intro2:
      'Kunskapsbasen består av två delar: en gemensam grund för alla Kuutio-workshops och en substansbas skräddarsydd för varje Kuutios ämne. Substansbasen har sammanställts av uppdragsgivaren. Deltagarna kunde också ta med centrala källor relaterade till ämnet. Uppdragsgivaren kuraterar källorna som deltagarna föreslagit.',
    totalUsed: 'Totalt använde Kuutio {{count}} källor.',
    adminOverlayNote:
      'Redigeringar, dolda rader och manuellt tillagda rader lever i rapportens överlagring (report_edits) — de synkas ännu inte till RAG-databasen. Sammanföring av listan till databasnivå kommer senare.',
    loading: 'Laddar kunskapsbas…',
    loadError: 'Inläsning av kunskapsbas misslyckades: {{error}}',
    baseAccordion: 'Grundkunskap — {{count}} källor',
    instanceAccordion: '{{topic}} — {{count}} källor',
    instanceTopicFallback: 'Kuutios egen kunskapsbas',
    empty: 'Inga källor i denna kunskapsbas.',
    colName: 'Namn',
    colPublisher: 'Utgivare',
    hiddenBadge: 'Dold',
    editedBadge: 'Redigerad',
    addedBadge: 'Tillagd manuellt',
    restoreTitle: 'Återställ synlighet',
    hideTitle: 'Dölj',
    show: 'Visa',
    hide: 'Dölj',
    addSourceTitle: 'Lägg till ny källa',
    addSource: 'Lägg till källa',
  },
  highlights: {
    participant: 'Deltagare',
    idea: 'Idé',
    obstacle: 'Hinder',
    solution: 'Lösning',
  },
  executive: {
    keyFindings: 'Viktigaste fynden',
    whatWeLearned: 'Vad lärde vi oss?',
    whatBlocks: 'Vad hindrar framsteg?',
    nextSteps: 'Nästa steg',
    selectedPerspective: 'Valt perspektiv',
  },
  excludedNote: '{{count}} felaktiga inmatningar utelämnades från AI-sammandraget.',
  gate: {
    title: 'Deltagarinnehål',
    hint: 'Logga in med hjälp av knappen Logga in överst för att se det här avsnittet.',
  },
  signIn: {
    title: 'Logga in till rapporten',
    hint: 'Ange workshopens PIN-kod för att se deltagarinnehål. Du kan få koden från workshoparrangören.',
    placeholder: 'PIN-kod',
    submit: 'Logga in',
    error: 'Fel PIN-kod.',
    emailHint: 'Om du har bjudits in att läsa rapporten, logga in med din e-postadress. Du får en engångskod per e-post.',
    useEmail: 'Logga in med e-post',
    usePin: 'Logga in med PIN-kod',
    noAccess: 'Du är inloggad som {{email}}, men kontot har ingen åtkomst till rapportens skyddade innehåll. Logga in med PIN-koden eller be arrangören om en inbjudan.',
    closeAriaLabel: 'Stäng',
  },
  common: {
    readMore: 'Läs mer',
    close: 'Stäng',
    hide: 'Dölj',
    showAll: 'Visa alla {{count}}',
  },
  lenses: {
    title: 'Hinder och möjligheter',
    obstacles: 'Hinder',
    opportunities: 'Möjligheter',
    aiSummary: 'AI-sammandrag',
    manualSource: 'Manuellt tillagd källa',
    expandMore: 'Visa alla {{count}}',
    expandLess: 'Visa mindre',
    introDefault:
      'Kuutios sex linser hjälper till att granska de identifierade vridpunkterna från flera håll och synliggöra relaterade hinder och möjligheter. Totalt identifierades hinder och möjligheter genom {{lensCount}} olika linser.',
    humanIntro:
      'Smågrupperna utforskade vridpunkter genom olika linser och identifierade hinder och möjligheter kring varje vridpunkt.',
    itemsCount: '{{count}} hinder/möjligheter',
    obstaclesCount: '{{count}} hinder',
    opportunitiesCount: '{{count}} möjligheter',
    allItemsTab: 'Alla punkter ({{count}})',
    noSummaryYet:
      'Inget sammandrag har ännu sammanställts för denna lins. Alla registrerade hinder och möjligheter finns på fliken ”Alla punkter”.',
    noItemsYet: 'Inga enskilda hinder eller möjligheter har registrerats för denna lins.',
    obstaclesWithCount: 'Hinder ({{count}})',
    opportunitiesWithCount: 'Möjligheter ({{count}})',
    obstacleShort: 'Hinder',
    opportunityShort: 'Möj.',
    obstacleAria: 'Hinder',
    opportunityAria: 'Möjlighet',
    summaryIdentified:
      'Deltagarna identifierade {{obstacleCount}} hinder och {{opportunityCount}} möjligheter genom sex linser. Flest hinder kom från {{topObstacleLens}}-linsen ({{topObstacleCount}}) och flest möjligheter från {{topOppLens}}-linsen ({{topOppCount}}).',
    summaryMoreOpportunities: ' Sammantaget vägde möjligheterna tyngre än hindren.',
    summaryMoreObstacles: ' Sammantaget vägde hindren tyngre än möjligheterna.',
    summaryBalanced: ' Hinder och möjligheter fördelades jämnt.',
  },
  solutions: {
    summaryGroups:
      '{{groupCount}} grupper producerade sammanlagt {{solutionCount}} lösningsförslag.',
    summaryAvgScore: ' I äldre analyser är det genomsnittliga helhetsbetyget {{avgScore}}/5.',
    summaryTagBased: ' Nyare analyser använder kvalitativa taggar utan helhetsbetyg.',
    printFullTitle: 'Lösningar — Fullständiga beskrivningar',
    voteTitle: '{{count}} deltagarröster',
    showUnvoted: 'Visa även andra lösningar',
    blockerAnswersTitle: 'Hur gruppen svarade på hindren',
    blockerAnswerLabel: 'Svar',
    blockerFallbackNote: 'Ingen AI-sammanfattning finns ännu; gruppens svar på hindren listas nedan.',
    blockerNarrativeLabel: 'Sammanfattning av hindersvar',
  },
  hybrids: {
    selectedNote:
      'Valda kombinationer nedan (taggar i nyare analyser; minst {{minScore}}/5 i helhetsbetyg i äldre).',
    badge: 'Kombination',
    fallbackSolution: 'Lösning',
    titleLabel: 'Hybridlösningens titel',
    descriptionLabel: 'Hybridlösningens beskrivning',
  },
  whatIf: {
    title: 'Tänk om?',
    imageAlt: 'Tänk om-fråga {{index}}',
    placeholder: 'Bildplatshållar saknas',
    addImage: 'Lägg till bild',
    altText: 'Alternativ text',
    question: 'Fråga',
    fromModule: 'Frågan kommer från modulen Tänk om, så den redigeras inte i rapporten.',
    imageUrl: 'Bild-URL',
    hint: 'Facilitatorn fyller i dessa tio "Tänk om?"-frågor baserat på teman eller framtidsbilder som framkom i workshoppen.',
    introDefault:
      'Framsyn handlar om att fånga helheten av förändringar och utforska alternativa framtider. Svaga signaler — tecken på möjliga förändringar som kan bli betydelsefulla i framtiden — är ett sätt att göra det. Framtiden är ofta överraskande överraskande. Därför behöver vi förmågan att vidga vårt tänkande om vad framtiden kan innehålla. Svaga signaler hjälper oss att fråga "Tänk om?". I Kuutio vidgar vi vårt tänkande med svaga signaler, wild cards och i förväg förberedda ämnesrelaterade "Tänk om?"-frågor.',
    creditsHint:
      'Frågorna skapades med Google Gemini Pro, men de valdes och finslipades av experter. Bilderna skapades med Adobe Firefly AI.',
    workshopAction: 'Fundera tillsammans på frågorna och hitta på egna nya "Tänk om?"-frågor.',
  },
  participantWhatIf: {
    title: 'Deltagarnas Tänk om-frågor',
    hint: '"Tänk om"-frågor som ställdes i smågrupper, baserat på transkriptet.',
  },
  scenarioChoice: {
    title: 'Smågruppernas val',
    hint: 'Varje grupps uttalande om sitt scenarioval, baserat på transkriptet.',
  },
  groupPhoto: {
    defaultAlt: 'Workshopens gruppfoto',
    openAriaLabel: 'Öppna gruppfotot större',
  },
  participants: {
    header: 'Deltagare',
    curatedSource: 'Välutvald',
    registeredSource: 'Registrerad',
    loading: 'Laddar deltagare…',
    error: 'Fel vid inläsning av deltagare',
    extras: '(Lägg här till eventuella saknade deltagare eller andra anteckningar.)',
    showAll: 'Visa alla {{count}}',
    hideAll: 'Dölj',
    listExport: 'Deltagarlista i PDF-export',
    intro:
      'En grupp centrala experter, beslutsfattare och aktörer med erfarenhetskunskap deltog i Kuutio-arbetet. Deras bidrag utgör rapportens innehållsmässiga kärna.',
    commissioner: 'Uppdragsgivare',
    team: 'Kuutio-teamet',
    noListHint:
      'Ingen deltagarlista tillgänglig. Kontrollera att data/kuutio_N/participants.md finns och att instansens metadata.kuutio_number är satt.',
    unnamed: 'Namnlös',
  },
  orgActions: {
    title: 'Åtgärder',
    overview: 'Överblick',
    intro: 'Hur börjar vi? AI:n har identifierat totalt {{actions}} konkreta åtgärder för {{orgs}} organisationer som främjar workshopens lösningar utifrån varje organisations egna styrkor. Av dessa kan {{now}} genomföras genast, {{soon}} under de närmaste veckorna och {{next}} därefter.',
    allActions: 'Alla åtgärder {{count}}',
    urgency: 'Brådskande',
    urgencyNow: 'Nu',
    urgencySoon: 'Snart',
    urgencyLater: 'Senare',
    effortSmall: 'Liten',
    effortMedium: 'Medel',
    effortLarge: 'Stor',
    responsible: 'Ansvarig',
    relatedSolution: 'Lösning: {{name}}',
    expandAll: 'Expandera alla',
    collapseAll: 'Komprimera alla',
    organisationsCount: '{{count}} organisationer',
    reasoning: 'Motivering',
    expandMore: 'Visa mer',
    expandLess: 'Visa mindre',
    noActions: 'Inga åtgärder',
  },
  firstSteps: {
    title: 'Vad ska göras först?',
    aiDisclaimer: 'AI-förslag',
    intro: 'Första fasen (0–3 månader) åtgärder från de sammanställda lösningarna, grupperade efter organisation:',
    expandOrg: 'Expandera {{name}}',
    noActions: 'Inga åtgärder under denna period',
    expandMore: 'Visa mer',
    expandLess: 'Visa mindre',
    zeroMonth: '0–3 månader',
    disclaimer: 'Detta är en AI-genererad uppmaning till handling — behandla den inte som en order. :)',
    noOrgAttribution:
      'Åtgärderna kan inte tillskrivas enskilda organisationer — se lösningspecifika planer i detaljerna.',
    unattributedNote:
      'Dessutom {{count}} åtgärder för vilka ingen tydlig ansvarig part identifierades — de visas i lösningspecifika planer.',
    actionCount: '{{count}} åtgärder',
    collaborationCta: 'Förslag till samarbete',
    findSolution: 'Hitta en lösning du kan gå med i direkt —',
    browseSolutions: 'bläddra bland lösningar',
  },
  analysis: {
    implementationOutlook: 'Genomförandebild',
    lensesTitle: 'Linser',
  },
  implementationPlan: {
    title: 'Hur börjar vi?',
    aiDisclaimer: 'AI-förslag',
    firstStep: 'Första steg',
    reasoningLabel: 'Motivering',
    leadOrg: 'LEDANDE ORG',
    supportOrg: 'STÖD',
    timeline: 'Tidslinje',
    expandTimeline: 'Expandera tidslinje',
    collapseTimeline: 'Komprimera tidslinje',
    phase0: '0–3 månader',
    phase1: '3–6 månader',
    phase2: '6–9 månader',
    phase3: '9–12 månader',
    risks: 'Risker',
    noRisks: 'Inga identifierade risker',
    disclaimer: 'Detta är en AI-genererad uppmaning till handling — behandla den inte som en order. :)',
    timelineExpand: '12-månaders genomförandeplan',
    yearAchievable: 'Vad som kan uppnås på ett år',
    yearAchieved: 'Vad som kan uppnås inom ett år',
    govtTermAchievable: 'Vad som kan uppnås under mandatperioden',
    govtTermDerived:
      '{{summary}} Under mandatperioden innebär detta en etablerad struktur, mogen lagstiftning och mätning av resultat nationellt — skalning baserad på framgångarna i årsplanen.',
    aiAccelerationNote:
      'Accelererande AI och digitalisering kan avsevärt påskynda genomförandet — många projekt som traditionellt tog år kan nu genomföras på månader.',
    govtTermPlaceholder:
      'En mer detaljerad vision för mandatperioden produceras i nästa planeringsrunda.',
    mitigation: 'Avhjälpning:',
    moreOrgsTitle: '{{count}} andra organisationer',
    moreOrgsShort: '+{{count}} till',
  },
  futureStory: {
    title: 'Önskad framtid',
    intro: 'Framtiden existerar för närvarande bara i vår fantasi. Fantasi är en medfödd egenskap hos varje människa som kan öppna dörrar till otaliga möjliga framtider. Det vi föreställer oss påverkar våra känslor och handlingar i nuet och därmed framtidens riktning.',
    whatLabel: 'Vad deltagarna föreställde sig',
    whatHint: 'Beskriv vad deltagarna föreställde sig — till exempel vilken sorts framtid den gemensamma fantasisjälsresan tog avseende ämnet.',
    whatDefault: 'Kuutio-arbetet börjar genom att utforska den önskade framtiden relaterad till ämnet. I denna Kuutio föreställde sig deltagarna…',
    storyLabel: 'Kollektiv framtidsbild',
    storyHint: 'Bifoga den AI-genererade kollektiva framtidsberättelsen skapad från de förändringar som deltagarna delade. Berättelsen kommer att visas i rapporten som den är.',
    storyTitle: 'Kollektiv framtidsbild',
  },
  transcript: {
    sectionTitle: 'Transkript',
    perspectiveLabel: 'Vald perspektiv',
    showAllPerspectives: 'Visa alla {{count}} alternativ',
    hideAllPerspectives: 'Dölj alternativ',
    speaker: 'Talare',
    facilitator: 'Facilitator',
    group: 'Grupp',
    emptyComments: 'Inga kommentarer.',
    speakersComments: '{{speakers}} talare · {{comments}} kommentarer',
    showSummary: 'Visa sammanfattning',
    showFullQuote: 'Visa hela yttrandet',
    hideTimeline: 'Dölj tidslinje',
    showAllComments: 'Visa alla {{count}} kommentarer',
  },
};
